import * as React from 'react'
import { FlatList, View, TouchableOpacity, Text } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Icon from '@components/icon/Icons'
import VirtualList from '@components/list/VirtualList'
import { Animation } from '@components/root/Animators'

export interface Option {
  label: string
  value: any
  disabled?: boolean
}

interface SelectedOption extends Option {
  selected: boolean
}

interface Props {
  data: Option[]
  onItemSelected: (opt: Option) => void
  numColumns?: number
  value?: Option
  inline?: boolean
  lines?: number
}

const ITEM_H = 80
const SEPARATOR_H = 15

const Choice = ({ data, onItemSelected, numColumns = 1, value, inline = true, lines = 1 }: Props) => {
  const scale = Math.max(1, lines / 2)

  // 1 line => x1, 2 lines => x1.5, 3 lines => x2...
  // const scale = (lines + 1) / 2

  const options: SelectedOption[] = React.useMemo(
    () => data.map((d) => ({ ...d, selected: !!value && value.value === d.value })),
    [data, value]
  )

  const didItemChanged = (prev: SelectedOption, next: SelectedOption) => prev.selected !== next.selected

  const renderItem = (item: SelectedOption, index: number) => {
    const position = index % numColumns

    if (numColumns > 1) {
      return (
        <Item key={item.value}>
          <Tab
            selected={item.selected}
            disabled={item.disabled}
            activeOpacity={0.9}
            onPress={() => onItemSelected(item)}
            positionInRow={position === 0 ? 'first' : position === numColumns - 1 ? 'last' : 'between'}
            scale={scale}>
            <TabText selected={item.selected} centered={numColumns !== 1} numberOfLines={lines}>
              {item.label}
            </TabText>
            {item.selected && numColumns === 1 && <Icon name="done" size={26} color={theme.colors.primary} />}
          </Tab>

          <Separator />
        </Item>
      )
    }

    return (
      <React.Fragment key={item.value}>
        <Animation>
          <Tab
            selected={item.selected}
            disabled={item.disabled}
            activeOpacity={0.9}
            onPress={() => onItemSelected(item)}
            positionInRow={position === 0 ? 'first' : position === numColumns - 1 ? 'last' : 'between'}
            scale={scale}>
            <TabText selected={item.selected} centered={numColumns !== 1} numberOfLines={lines}>
              {item.label}
            </TabText>
            {item.selected && numColumns === 1 && <Icon name="done" size={26} color={theme.colors.primary} />}
          </Tab>
        </Animation>

        <Separator />
      </React.Fragment>
    )
  }

  if (inline) {
    return (
      <FlatList
        data={options}
        keyExtractor={(item, idx) => `${idx}`}
        renderItem={({ item, index }) => renderItem(item, index)}
        numColumns={numColumns}
      />
    )
  }

  return (
    <VirtualList
      key={data.length}
      data={options}
      height={scale * ITEM_H + SEPARATOR_H}
      renderItem={renderItem}
      didItemChanged={didItemChanged}
    />
  )
}

export default Choice

const Tab = styled(TouchableOpacity)<{
  selected: boolean
  positionInRow: 'first' | 'last' | 'between'
  disabled?: boolean
  scale: number
}>`
  flex-direction: row;
  align-items: center;
  padding: 0 16px;
  border-radius: 8px;
  background-color: ${(props) => (props.selected ? props.theme.colors.secondaryActive : props.theme.colors.background)};
  border: 1px solid ${(props) => (props.selected ? props.theme.colors.primary : props.theme.colors.secondaryActive)};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  height: ${(props) => props.scale * ITEM_H}px;
  ${(props) =>
    `
    margin: ${
      props.positionInRow === 'first'
        ? '0px 4px 0px 0px'
        : props.positionInRow === 'last'
        ? '0px 0px 0px 4px'
        : '0px 4px'
    };
    `}
`

const TabText = styled(Text)<{ selected: boolean; centered: boolean }>`
  ${(props) => props.theme.fonts.Body};
  ${(props) => props.centered && `text-align: center;`}
  flex: 1;
  margin: 6px 0;
  margin-right: 16px;
`

const Separator = styled(View)`
  margin-bottom: ${SEPARATOR_H}px;
`

const Item = styled(View)`
  flex: 1;
`
